import * as CL from '@design-system/component-library';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { dsClass } from '../../../common/constants/dsClasses.js';
import { copyUserInfoMsg, t } from '../../../common/i18n/index.js';
import { EMAIL_REGEX } from '../../../common/utils/emailUtils.js';
import { PHONE_NUMBER_REGEX } from '../../../common/utils/phoneNumberUtils.js';
import { COST_CENTER_REGEX } from '../../../common/utils/validationUtils.js';
import { ContactCreationType, formValuesToPurposeOfUseOrContact } from '../ContactsOrPurposeOfUseUtils.js';
import type { PurposeOfUseOrContact } from '../../../common/types/subscription.js';
import type { ContactOrPurposeOfUseFormValues } from '../ContactsOrPurposeOfUseUtils.js';

interface CopyUserInfoButtonProps {
  onCopyUserInfo: (purposeOfUseOrContact: PurposeOfUseOrContact) => void;
  isEmailAndPhoneRequired?: boolean;
  isCostCenterRequired?: boolean;
}

export const CopyUserInfoButton = ({
  onCopyUserInfo,
  isEmailAndPhoneRequired,
  isCostCenterRequired,
}: CopyUserInfoButtonProps) => {
  const {
    watch,
    formState: { isDirty },
  } = useFormContext<ContactOrPurposeOfUseFormValues>();
  const values = watch();
  const [disabled, setDisabled] = useState(false);

  // Custom validation — needed because conditionally visible fields make form-level isValid unreliable
  const isValid = useMemo(
    () =>
      (values.selectContact || values.purposeOfUse) &&
      (values.selectContact !== ContactCreationType.CREATE_NEW_CONTACT ||
        (values.firstName &&
          values.lastName &&
          (!isEmailAndPhoneRequired ||
            (values.email &&
              EMAIL_REGEX.test(values.email) &&
              values.phoneNumber &&
              PHONE_NUMBER_REGEX.test(values.phoneNumber))))) &&
      (!isCostCenterRequired || (values.costCenter && COST_CENTER_REGEX.test(values.costCenter))),
    [
      isCostCenterRequired,
      isEmailAndPhoneRequired,
      values.costCenter,
      values.email,
      values.firstName,
      values.lastName,
      values.phoneNumber,
      values.purposeOfUse,
      values.selectContact,
    ]
  );

  useEffect(() => {
    if (isDirty) {
      setDisabled(false);
    }
  }, [isDirty]);

  return (
    <CL.Button
      color="link"
      onClick={() => {
        setDisabled(true);
        onCopyUserInfo(formValuesToPurposeOfUseOrContact(values));
      }}
      disabled={!isValid || disabled}
      className={`copy-user-info-button ${dsClass.FONT_WEIGHT_NORMAL} ${dsClass.FONT_SIZE_MEDIUM} ${dsClass.PADDING_LEFT_0}`}
      size="l"
    >
      <>
        <CL.Icon icon="update" size="m" className={dsClass.MARGIN_RIGHT_1} />
        {t.GUWZ(copyUserInfoMsg)}
      </>
    </CL.Button>
  );
};
